/**
 * Network Diagnostics
 *
 * Passive instrumentation for detecting hanging network requests.
 * Logs to Datadog when requests are stale (pending > threshold) or fail.
 *
 * Usage in actors:
 *   const tracker = trackNetworkRequest("loadHrts");
 *   try {
 *     const result = await someServerAction();
 *     tracker.success();
 *     return result;
 *   } catch (error) {
 *     tracker.error(error);
 *     throw error;
 *   }
 */

import { datadogLogs } from "@datadog/browser-logs";
import { traceInspector } from "../devtool/trace-inspector";

// ============================================================================
// Configuration
// ============================================================================

const STALE_THRESHOLD_MS = 10_000; // Log warning if pending > 10s
const STALE_CHECK_INTERVAL_MS = 5_000; // Check for stale requests every 5s
const SLOW_REQUEST_THRESHOLD_MS = 3_000; // Log info if request takes > 3s

// ============================================================================
// Types
// ============================================================================

interface PendingRequest {
  action: string;
  startedAt: number;
  context?: RequestContext;
}

interface RequestContext {
  practiceId?: string;
  orgId?: number;
  appointmentId?: string;
}

interface NetworkState {
  onLine: boolean;
  rtt?: number;
  downlink?: number;
  effectiveType?: string;
}

// ============================================================================
// State
// ============================================================================

const pendingRequests = new Map<string, PendingRequest>();
let sessionStartTime: number | null = null;

// ============================================================================
// Helpers
// ============================================================================

function getNetworkState(): NetworkState {
  const conn = (navigator as unknown as { connection?: NetworkState })
    .connection;
  return {
    onLine: navigator.onLine,
    rtt: conn?.rtt,
    downlink: conn?.downlink,
    effectiveType: conn?.effectiveType,
  };
}

/**
 * Measure main thread blocking by checking how long it takes for a
 * microtask to execute. High latency = main thread is busy.
 *
 * Uses MessageChannel which is more accurate than requestIdleCallback
 * (better Safari/iOS support) and measures microtask queue latency.
 */
function getMainThreadBlockingMs(): Promise<number> {
  return new Promise((resolve) => {
    const startTime = performance.now();

    // Primary: Use MessageChannel for accurate microtask measurement (works everywhere)
    if (typeof MessageChannel !== "undefined") {
      const { port1, port2 } = new MessageChannel();
      port1.onmessage = () => {
        const latencyMs = performance.now() - startTime;
        resolve(latencyMs);
      };
      port2.postMessage(null);
    } else if (typeof requestIdleCallback !== "undefined") {
      // Fallback: requestIdleCallback if available
      requestIdleCallback(() => {
        const latencyMs = performance.now() - startTime;
        resolve(latencyMs);
      });
    } else {
      // Last resort: setTimeout (least accurate but always available)
      setTimeout(() => {
        const latencyMs = performance.now() - startTime;
        resolve(latencyMs);
      }, 0);
    }
  });
}

/**
 * Get localStorage items by prefix to track accumulation by source
 */
function getStorageByPrefix(
  prefix: string,
): { key: string; sizeBytes: number }[] {
  const items: { key: string; sizeBytes: number }[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(prefix)) {
      const value = localStorage.getItem(key) || "";
      items.push({
        key,
        sizeBytes: new Blob([value]).size,
      });
    }
  }
  return items;
}

/**
 * Get sizes of known storage keys/patterns that accumulate
 */
function getStorageAccumulation(): Record<string, number> {
  return {
    mixpanel_events: getStorageByPrefix("__mpq_").reduce(
      (sum, item) => sum + item.sizeBytes,
      0,
    ),
    kiosk_context: getStorageByPrefix("kiosk_").reduce(
      (sum, item) => sum + item.sizeBytes,
      0,
    ),
    session_data: getStorageByPrefix("session_").reduce(
      (sum, item) => sum + item.sizeBytes,
      0,
    ),
  };
}

function getSessionId(): string | undefined {
  return traceInspector.getTrace()?.traceId;
}

function getSessionAgeMs(): number | null {
  if (!sessionStartTime) return null;
  return Date.now() - sessionStartTime;
}

// ============================================================================
// Stale Request Monitor
// ============================================================================

let staleCheckInterval: ReturnType<typeof setInterval> | null = null;

function startStaleRequestMonitor() {
  if (staleCheckInterval) return;

  staleCheckInterval = setInterval(() => {
    const now = Date.now();

    for (const [requestId, req] of pendingRequests) {
      const elapsed = now - req.startedAt;

      if (elapsed > STALE_THRESHOLD_MS) {
        datadogLogs.logger.warn(`Kiosk Network request stale: ${req.action}`, {
          requestId,
          action: req.action,
          elapsedMs: elapsed,
          kioskSessionId: getSessionId(),
          practiceId: req.context?.practiceId,
          orgId: req.context?.orgId,
          appointmentId: req.context?.appointmentId,
          network: getNetworkState(),
          sessionAgeMs: getSessionAgeMs(),
          environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
          tags: ["KIOSK", "NETWORK", "STALE"],
        });
      }
    }
  }, STALE_CHECK_INTERVAL_MS);
}

// Start monitor on module load (client-side only)
if (typeof window !== "undefined") {
  startStaleRequestMonitor();
  sessionStartTime = Date.now();

  // Expose for debugging in console
  (
    window as unknown as { __networkDiagnostics?: object }
  ).__networkDiagnostics = {
    getPendingRequests: () => {
      const now = Date.now();
      return Array.from(pendingRequests.entries()).map(([id, req]) => ({
        id,
        action: req.action,
        elapsedMs: now - req.startedAt,
        context: req.context,
      }));
    },
    getNetworkState,
    getSessionId,
    getSessionAgeMs,
    getMainThreadBlockingMs,
    getStorageAccumulation,
  };
}

// ============================================================================
// Main API
// ============================================================================

/**
 * Track a network request for diagnostics.
 * Returns success/error callbacks to mark completion.
 *
 * @param action - Name of the action (e.g., "loadHrts", "createAppointment")
 * @param context - Optional context for correlation (practiceId, orgId, etc.)
 */
export function trackNetworkRequest(
  action: string,
  context?: RequestContext,
): {
  success: () => void;
  error: (error: unknown) => void;
} {
  const requestId = `${action}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 6)}`;
  const startedAt = Date.now();

  pendingRequests.set(requestId, {
    action,
    startedAt,
    context,
  });

  // TODO: After debugging, remove this start log
  // Log ALL request starts to debug hanging issue
  datadogLogs.logger.info(`Kiosk Network request started: ${action}`, {
    requestId,
    action,
    kioskSessionId: getSessionId(),
    practiceId: context?.practiceId,
    orgId: context?.orgId,
    appointmentId: context?.appointmentId,
    network: getNetworkState(),
    sessionAgeMs: getSessionAgeMs(),
    environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
    tags: ["KIOSK", "NETWORK", "STARTED"],
  });

  return {
    success: () => {
      // DEBUG: Console log to verify success() is called (more reliable than Datadog)
      console.log(`[NetworkDiagnostics] success() called for ${action}`, {
        requestId,
        timestamp: Date.now(),
      });

      const req = pendingRequests.get(requestId);
      if (!req) {
        console.warn(
          `[NetworkDiagnostics] No pending request found for ${requestId}`,
        );
        return;
      }

      const durationMs = Date.now() - req.startedAt;
      pendingRequests.delete(requestId);

      const isSlow = durationMs > SLOW_REQUEST_THRESHOLD_MS;

      // DEBUG: Log before Datadog call
      console.log("[NetworkDiagnostics] About to log to Datadog", {
        requestId,
        action,
        durationMs,
      });

      // TODO: After debugging, revert to only logging slow requests
      // Log ALL completions for now to debug hanging issue
      datadogLogs.logger.info(
        `Kiosk Network request ${isSlow ? "slow" : "success"}: ${action}`,
        {
          requestId,
          action,
          durationMs,
          kioskSessionId: getSessionId(),
          practiceId: req.context?.practiceId,
          orgId: req.context?.orgId,
          appointmentId: req.context?.appointmentId,
          network: getNetworkState(),
          sessionAgeMs: getSessionAgeMs(),
          environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
          tags: ["KIOSK", "NETWORK", isSlow ? "SLOW" : "COMPLETED"],
        },
      );
    },

    error: (error: unknown) => {
      const req = pendingRequests.get(requestId);
      if (!req) return;

      const durationMs = Date.now() - req.startedAt;
      pendingRequests.delete(requestId);

      datadogLogs.logger.warn(`Kiosk Network request failed: ${action}`, {
        requestId,
        action,
        durationMs,
        error: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : undefined,
        kioskSessionId: getSessionId(),
        practiceId: req.context?.practiceId,
        orgId: req.context?.orgId,
        appointmentId: req.context?.appointmentId,
        network: getNetworkState(),
        sessionAgeMs: getSessionAgeMs(),
        environment: process.env.NEXT_PUBLIC_SAM_ENV || "local",
        tags: ["KIOSK", "NETWORK", "ERROR"],
      });
    },
  };
}
